import React, { useCallback, useEffect, useRef, useState } from "react";
import CustomButton from "../components/CustomButton";
import CustomCards from "../components/CustomCards";
import Notification from "../components/Notification";
import Pagination from "../components/CustomPagination";
import { useProviderData } from "../custom-hooks/useProvidersData";
import { useDiscoveryGridSort } from "../custom-hooks/useDiscoveryGridSort";
import {
  GlobeAltIcon,
  ListBulletIcon,
  Squares2X2Icon,
} from "@heroicons/react/24/outline";
import ListGrid from "../components/ListGrid";
import CustomCardsSkeleton from "../components/CustomCardsSkeleton";
import SkeletonLoader from "../components/SkeletonLoader";
import {
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { useDispatch, useSelector } from "react-redux";
import { useScreenWidth } from "../custom-hooks/useScreenWidth";
import { debounce } from "lodash";
import { setSearchTextByPage } from "../redux-store/features/providerFiltersSlice";
import MainSearchBar from "../components/MainSearchbar";
import CustomConfirmModal from "../components/CustomConfirmModal";
import HorizontalScrollHint from "../components/CustomHorizontalScroll";
import { setDrawer } from "../redux-store/features/layoutSlice";
import { useNavigate } from "react-router-dom";
import { useExport } from "../custom-hooks/queries";
import { useMemo } from "react";
import Modal from "../components/Modal";
import { setVendorsFilters } from "../redux-store/features/indicationsDashboardFiltersSlice";
import { setProvidersFilters as setDiscoveryFilters } from "../redux-store/features/providerFiltersSlice";
import { ChatDQPButton } from "../components/ChatDQP/ChatDQPButton";
import { VendorFilters } from "../components/Provider/VendorFilters";
import VendorDiscoverySelectionBar from "../components/Provider/VendorDiscoverySelectionBar";
import DataLoading from "../components/DataLoading";
import ProvidersMapView from "../components/Provider/ProvidersMapView";
import ProviderCardList from "../components/Provider/ProviderCardList";
import ProvidersListGrid from "../components/Provider/ProviderListGrid";
import { useRfiListForFilters } from "../custom-hooks/useFiltersData-hooks";
import { useUser } from "@clerk/clerk-react";
import { userHasRight, useUserRole, isBackendViewOnlyUser } from "../utils/helperMethods";
import { useDecodedToken } from "../custom-hooks/useToken";
import useMyVendorMembershipActions from "../custom-hooks/useMyVendorMembershipActions";
import useMyVendors from "../custom-hooks/useMyVendors";
import useMyVendorLists from "../custom-hooks/useMyVendorLists";
import useMyVendorListActions from "../custom-hooks/useMyVendorListActions";
import useBookmarkedVendors from "../hooks/useBookmarkedVendors";
import AddToListModal from "../components/MyVendors/AddToListModal";
import CreateVendorListModal from "../components/MyVendors/CreateVendorListModal";
import AddToMyVendorsModal from "../components/MyVendors/AddToMyVendorsModal";
import { MY_VENDORS_REQUIRED_RIGHT, canUseNewMyVendorsAffordances } from "../utils/myVendorsFilterOptions";
import { SYSTEM_LIST_IDS } from "../utils/myVendorListsConstants";
import { broadcastMyVendorsChanged } from "../utils/myVendorMembershipBroadcast";
import { resolveVendorProviderId } from "../utils/myVendorListsUtils";
import useOrgVqaOnlyStatus from "../custom-hooks/useOrgVqaOnlyStatus";
import {
  canUseChatDiligent,
  canUseTrialExperienceDashboard,
} from "../utils/orgFeatureGates";

const Discovery = () => {
  const [show, setShow] = useState(false);
  const [cardView, setCardView] = useState("card");
  const {
    sortColumn,
    sortDirection,
    sortFetchNonce,
    currentPage,
    setCurrentPage,
    discoverySortParams,
    handleSort,
  } = useDiscoveryGridSort();
  const [filtersBool, setFiltersBool] = useState(false);
  const [open, setOpen] = useState(false);
  const [isExport, setIsExport] = useState(false);
  const [exportError, setExportError] = useState(false);
  const [noDocs, setNoDocs] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [serviceColors, setServiceColors] = useState("");
  const searchInputRef = useRef(null);

  const [selectedVendors, setSelectedVendors] = useState([]);
  const [createListModalOpen, setCreateListModalOpen] = useState(false);
  const [addToListModalOpen, setAddToListModalOpen] = useState(false);
  const [addToMyVendorsModalOpen, setAddToMyVendorsModalOpen] = useState(false);
  const [modalSelectedVendors, setModalSelectedVendors] = useState([]);
  const [addToListSubmitting, setAddToListSubmitting] = useState(false);
  const [myVendorsNotification, setMyVendorsNotification] = useState({
    show: false,
    message: "",
    status: "success",
  });
  const [pendingBookmarkIds, setPendingBookmarkIds] = useState({});
  const [bulkBookmarkPending, setBulkBookmarkPending] = useState(false);
  const [noDocsToExport, setNoDocsToExport] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const providerIds = useSelector(
    (state) => state.providersFilters.providerIds,
  );

  const { rights } = useSelector((state) => state.userRights);
  const { user } = useUser();
  const loggedInUser = useSelector((state) => state.loggedInUser);
  const hasChatDiligentAccess = canUseChatDiligent(loggedInUser);

  // Check if user is Admin to hide request capabilities
  const userRole = useUserRole();
  const subRoles = user?.publicMetadata?.sub_role || [];
  const hideRequestActions = isBackendViewOnlyUser(userRole);
  // Until the profile says, vendors' VQQs stay out of sight rather than show to a VQA only org.
  const hideVqq = useOrgVqaOnlyStatus() !== false;
  const { decoded } = useDecodedToken();
  const sponsorId = decoded?.sponsor_id ?? null;
  const hasMyVendorsRight =
    !hideRequestActions && userHasRight(rights, MY_VENDORS_REQUIRED_RIGHT);
  const canManageMyVendors = canUseNewMyVendorsAffordances(hasMyVendorsRight);
  const canCompareTrialExperience = canUseTrialExperienceDashboard(loggedInUser, rights);
  const canSelectVendors = canManageMyVendors || canCompareTrialExperience;
  const myVendorsSponsorId = canManageMyVendors ? sponsorId : null;

  const {
    getMembership,
    handleAddToMyVendors: addSingleManualVendor,
    handleRemoveFromMyVendors,
    handleAddToHidden,
    handleUnhide,
    isMutating: membershipMutating,
  } = useMyVendorMembershipActions(myVendorsSponsorId);

  const { providerIds: myVendorsProviderIds, vendors: myVendors } = useMyVendors(
    myVendorsSponsorId
  );
  const {
    bookmarkedProviderIds,
    isBookmarked,
    toggleBookmark,
    addBookmarks,
    toggleBookmarksForSelection,
  } = useBookmarkedVendors(myVendorsSponsorId);
  const {
    lists: vendorLists,
    userLists: userVendorLists,
  } = useMyVendorLists(myVendorsSponsorId, {
    myVendors,
    bookmarkedProviderIds,
  });
  const {
    createList,
    addMembersToList,
    isMutating: listMutating,
  } = useMyVendorListActions(myVendorsSponsorId);

  const showMyVendorsFeedback = useCallback((message, status = "success") => {
    setMyVendorsNotification({ show: true, message, status });
  }, []);

  const handleCreateListSingle = useCallback((vendor) => {
    setModalSelectedVendors([vendor]);
    setCreateListModalOpen(true);
  }, []);

  const handleAddToListSingle = useCallback((vendor) => {
    setModalSelectedVendors([vendor]);
    setAddToListModalOpen(true);
  }, []);

  const handleToggleBookmark = useCallback(
    async (vendorOrProvider) => {
      const providerId = String(vendorOrProvider?.id || "");
      if (!providerId) return;
      setPendingBookmarkIds((prev) => ({ ...prev, [providerId]: true }));
      try {
        await toggleBookmark(providerId);
      } catch (error) {
        console.error("Bookmark toggle failed:", error);
        showMyVendorsFeedback("Could not update bookmark. Please try again.", "error");
      } finally {
        setPendingBookmarkIds((prev) => {
          const next = { ...prev };
          delete next[providerId];
          return next;
        });
      }
    },
    [showMyVendorsFeedback, toggleBookmark]
  );

  const handleAddVendorsToList = useCallback(
    async (list, vendors) => {
      setAddToListSubmitting(true);
      try {
        if (list.id === SYSTEM_LIST_IDS.BOOKMARKED) {
          await addBookmarks(vendors.map((vendor) => resolveVendorProviderId(vendor)).filter(Boolean));
          return;
        }
        if (list.id === SYSTEM_LIST_IDS.MY_VENDORS) {
          await Promise.all(
            vendors.map((vendor) =>
              addSingleManualVendor(
                { id: resolveVendorProviderId(vendor) },
                { skipBroadcast: true }
              )
            )
          );
          broadcastMyVendorsChanged(sponsorId);
          return;
        }
        await addMembersToList(list.id, vendors);
      } catch (error) {
        console.error("Add to list failed:", error);
        showMyVendorsFeedback(
          error?.response?.data || "Could not add vendors to list. Please try again.",
          "error"
        );
        throw error;
      } finally {
        setAddToListSubmitting(false);
      }
    },
    [addBookmarks, addMembersToList, addSingleManualVendor, showMyVendorsFeedback, sponsorId]
  );

  const handleCreateVendorList = useCallback(
    async (payload) => {
      await createList(payload);
    },
    [createList]
  );

  const handleAddVendorsFromModal = useCallback(
    async (providerIdsToAdd) => {
      await Promise.all(
        providerIdsToAdd.map((providerId) =>
          addSingleManualVendor({ id: providerId }, { skipBroadcast: true })
        )
      );
      broadcastMyVendorsChanged(sponsorId);
    },
    [addSingleManualVendor, sponsorId]
  );

  const allSelectedBookmarked =
    selectedVendors.length > 0 &&
    selectedVendors.every((vendor) => isBookmarked(vendor.id));

  const handleBookmarkSelected = useCallback(async () => {
    if (!selectedVendors.length || bulkBookmarkPending) return;
    setBulkBookmarkPending(true);
    try {
      const providerIds = selectedVendors
        .map((vendor) => resolveVendorProviderId(vendor))
        .filter(Boolean);
      const result = await toggleBookmarksForSelection(providerIds);
      if (!result) return;

      if (result.action === "bookmarked") {
        const added = result.addedCount ?? 0;
        const skipped = result.alreadyBookmarkedCount ?? 0;
        if (added === 0 && skipped > 0) {
          showMyVendorsFeedback(
            `${skipped} vendor${skipped !== 1 ? "s" : ""} already bookmarked.`
          );
          return;
        }
        const addedLabel = `${added} vendor${added !== 1 ? "s" : ""} bookmarked`;
        const skippedLabel =
          skipped > 0
            ? `. ${skipped} already bookmarked and skipped.`
            : ".";
        showMyVendorsFeedback(`${addedLabel}${skippedLabel}`);
        return;
      }

      const removed = result.removedCount ?? 0;
      showMyVendorsFeedback(
        `${removed} vendor${removed !== 1 ? "s" : ""} removed from bookmarks.`
      );
    } catch (error) {
      console.error("Bookmark selected failed:", error);
      showMyVendorsFeedback("Could not update bookmarks. Please try again.", "error");
    } finally {
      setBulkBookmarkPending(false);
    }
  }, [
    bulkBookmarkPending,
    selectedVendors,
    showMyVendorsFeedback,
    toggleBookmarksForSelection,
  ]);

  const handleBulkAddToMyVendors = useCallback(async () => {
    const eligible = selectedVendors.filter(
      (vendor) => !getMembership(vendor.id)?.isInMyVendors
    );
    if (eligible.length === 0) {
      setModalSelectedVendors(selectedVendors);
      setAddToMyVendorsModalOpen(true);
      return;
    }

    const skipped = selectedVendors.length - eligible.length;
    try {
      await Promise.all(
        eligible.map((vendor) =>
          addSingleManualVendor(vendor, { skipBroadcast: true })
        )
      );
      broadcastMyVendorsChanged(sponsorId);

      const addedLabel = `${eligible.length} vendor${eligible.length !== 1 ? "s" : ""} added to My Vendors`;
      const skippedLabel =
        skipped > 0
          ? ` ${skipped} already in My Vendors and skipped.`
          : ".";
      showMyVendorsFeedback(`${addedLabel}.${skippedLabel}`);
    } catch (error) {
      console.error("Bulk add to My Vendors failed:", error);
      showMyVendorsFeedback("Could not add vendors to My Vendors. Please try again.", "error");
    }
  }, [
    addSingleManualVendor,
    getMembership,
    selectedVendors,
    showMyVendorsFeedback,
    sponsorId,
  ]);

  const myVendorDiscoveryProps = useMemo(
    () =>
      canManageMyVendors && sponsorId
        ? {
            canManageMyVendors: true,
            getMyVendorMembership: getMembership,
            onAddToMyVendors: addSingleManualVendor,
            onRemoveFromMyVendors: handleRemoveFromMyVendors,
            onAddToHidden: handleAddToHidden,
            onUnhide: handleUnhide,
            onCreateList: handleCreateListSingle,
            onAddToList: handleAddToListSingle,
            isBookmarked,
            onToggleBookmark: handleToggleBookmark,
            isBookmarkPending: (providerId) =>
              Boolean(pendingBookmarkIds[String(providerId || "")]),
          }
        : { canManageMyVendors: false },
    [
      addSingleManualVendor,
      canManageMyVendors,
      getMembership,
      handleAddToHidden,
      handleAddToListSingle,
      handleCreateListSingle,
      handleRemoveFromMyVendors,
      handleToggleBookmark,
      handleUnhide,
      isBookmarked,
      pendingBookmarkIds,
      sponsorId,
    ]
  );

  const rfiIds = useSelector((state) => state.providersFilters.rfiIds);
  const indications = useSelector(
    (state) => state.providersFilters.indications,
  );
  const providerTypes = useSelector(
    (state) => state.providersFilters.providerTypes,
  );
  const companySizeFiltersId = useSelector(
    (state) => state.providersFilters.companySizeFiltersId,
  );
  const selectedRevenueRange = useSelector(
    (state) => state.providersFilters.selectedRevenueRange,
  );
  const revenueRangeString = useSelector(
    (state) => state.providersFilters.revenueRangeString,
  );
  const reportAvailable = useSelector(
    (state) => state.providersFilters.reportAvailable,
  );
  const dataAvailability = useSelector(
    (state) => state.providersFilters.dataAvailability,
  );
  const countries = useSelector((state) => state.providersFilters.countries);
  const states = useSelector((state) => state.providersFilters.states);
  const cities = useSelector((state) => state.providersFilters.cities);
  const searchQuery = useSelector(
    (state) => state.providersFilters.searchTextByPage["discovery"],
  );

  const [browser, setBrowser] = useState("");

  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();

    if (userAgent.includes("edg")) {
      setBrowser("edge");
    } else if (userAgent.includes("chrome") && !userAgent.includes("edg")) {
      setBrowser("chrome");
    } else if (userAgent.includes("firefox")) {
      setBrowser("firefox");
    } else if (userAgent.includes("safari") && !userAgent.includes("chrome")) {
      setBrowser("safari");
    } else {
      setBrowser("unknown");
    }
  }, []);

  const { rfiListForFilters } = useRfiListForFilters();
  const mappingRfisColor = () => {
    // Map `services` to match their colors from `rfis`
    const colors = rfiListForFilters?.reduce((prev, service) => {
      prev[service.rfi_name] = {
        rfi_id: service.rfi_id,
        bgColor: service.rfi_color_code_bg ?? "#e0e0e0",
        textColor: service.rfi_color_code_text ?? "#000",
        borderColor: service.rfi_color_code_border ?? "#ccc",
        name: service.rfi_name,
      };
      return prev;
    }, {});

    setServiceColors(colors || []);
  };

  useEffect(() => {
    mappingRfisColor();
  }, [rfiListForFilters]);

  const {
    isLoading,
    isError,
    providers,
    totalPages,
    errorMessage,
    totalItems,
  } = useProviderData(
    currentPage,
    providerIds,
    rfiIds,
    providerTypes,
    indications,
    companySizeFiltersId,
    selectedRevenueRange,
    revenueRangeString,
    reportAvailable,
    dataAvailability,
    countries,
    states,
    cities,
    searchQuery,
    discoverySortParams,
    sortFetchNonce,
  );

  const skipPageResetOnMount = useRef(true);

  useEffect(() => {
    if (skipPageResetOnMount.current) {
      skipPageResetOnMount.current = false;
      setNoDocsToExport(false);
      return;
    }

    setCurrentPage(1);
    setNoDocsToExport(false);
  }, [
    providerIds,
    rfiIds,
    indications,
    providerTypes,
    companySizeFiltersId,
    selectedRevenueRange,
    revenueRangeString,
    reportAvailable,
    dataAvailability,
    filtersBool,
    searchQuery,
    setCurrentPage,
  ]);

  useEffect(() => {
    if (isError) {
      setShow(true);
    }
  }, [isError]);

  const debouncedOnSubmit = useCallback(
    debounce((search) => {
      dispatch(
        setSearchTextByPage({
          pageKey: "discovery",
          searchText: search,
        }),
      );
      setSearchInputValue(search);
    }, 300),
    [dispatch],
  );

  const onSubmit = (search) => {
    debouncedOnSubmit(search);
  };

  const screenWidth = useScreenWidth();
  const isMobile = screenWidth <= 480;
  const scrollHint = screenWidth <= 1024;

  const handleModal = () => {
    dispatch(setDrawer(true));
    setOpen(true);
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (providers?.length === 0) {
        setNoDocs(true);
      } else {
        setNoDocs(false);
      }
    }, 300);

    // Clear timeout if the component unmounts or providers changes
    return () => clearTimeout(timeout);
  }, [providers]);

  useEffect(() => {
    dispatch(
      setVendorsFilters({
        providerIds: selectedVendors?.map((d) => d.id) ?? [],
      }),
    );
  }, [dispatch, selectedVendors]);

  const renderProviders = (providersList) => {
    if (isLoading) {
      return cardView === "card" ? (
        <CustomCardsSkeleton browser={browser} isProviderDetail={false} />
      ) : (
        <SkeletonLoader />
      );
    }

    if (isError) {
      return (
        <div
          className="flex flex-col items-center text-center space-y-2"
          style={{
            marginTop: "200px",
            marginLeft: !isMobile ? "342.9px" : "0px",
          }}
        >
          <span className="p-3 rounded-full bg-custom-link-bg-color">
            <ExclamationTriangleIcon
              width={25}
              height={25}
              style={{ color: "white" }}
            />
          </span>
          <p className="text-lg text-gray-700 font-semibold">{isError}</p>
        </div>
      );
    }

    if (noDocs && providersList.length === 0) {
      return (
        <div
          className="flex flex-col items-center text-center space-y-2 "
          style={{
            marginTop: "200px",
            marginLeft: !isMobile ? "200px" : "0px",
          }}
        >
          <span className="p-3 rounded-full bg-custom-link-bg-color">
            <MagnifyingGlassIcon
              width={20}
              height={20}
              style={{ color: "white" }}
            />
          </span>
          <p className="text-lg text-gray-700 font-semibold">
            {noDocsToExport ? "No documents to export" : "No vendors found"}
          </p>
          <p className="text-gray-500">
            {!noDocsToExport
              ? "Your search did not match any vendors. Please try again."
              : "There are no documents to export. Please try again."}
          </p>
        </div>
      );
    }

    return cardView === "card" ? (
      providersList?.map((provider, index) => (
        <div
          key={index}
          className={`mb-8 ml-0 md:ml-[1.45rem] 
            ${
              browser === "edge"
                ? "ml-0 md:ml-[1.3rem]"
                : "ml-0 md:ml-[1.85rem]"
            }
          `}
        >
          <CustomCards
            type={provider?.provider_type}
            name={provider?.name}
            address1={provider?.address1}
            id={provider?.id}
            services={provider.provider_capabilities}
            city={provider.city}
            country={provider.country}
            rfi={provider.rfi_count}
            assessment={provider.assessment_count}
            badgeColor={provider?.provider_capabilities?.map(
              (capability) => capability.rfi_template_color_code,
            )}
            documents={true}
            imageUrl={provider.logo}
            cardView={cardView}
          />
        </div>
      ))
    ) : (
      <>
        {scrollHint && isMobile ? (
          <HorizontalScrollHint>
            <div className="w-80 sm:w-full lg:w-96 xl:w-full overflow-x-auto h-auto mt-3 bg-white rounded-2xl ml-0 md:w-width-30rem">
              <ListGrid providerFilters={providersList} />
            </div>
          </HorizontalScrollHint>
        ) : (
          <div className="w-full sm:w-full lg:w-96 xl:w-full h-auto mt-3 bg-white rounded-2xl ml-0  md:w-width-30rem">
            <ListGrid providerFilters={providersList} />
          </div>
        )}
      </>
    );
  };

  // Server owns sort order (including default logo-first). Do not re-sort the page client-side.
  const sortedProviders = providers;

  // Pagination state (must be after sortedProviders is defined)
  const [providersPage, setProvidersPage] = useState(1);
  const pageSize = 27;
  const totalResults = sortedProviders.length;
  const pagedProviders = sortedProviders.slice(
    (providersPage - 1) * pageSize,
    providersPage * pageSize,
  );

  const includeReportAvailable =
    reportAvailable?.is_rfi_available === true ||
    reportAvailable?.is_audit_report_available === true;

  const includeRangeRevenue =
    selectedRevenueRange?.min != null && selectedRevenueRange?.max != null;

  const payload = useMemo(
    () => ({
      page_size: 21,
      current_page: 1,
      ...(providerIds?.length && { provider_id_list: providerIds }),
      ...(rfiIds?.length && { rfi_template_id_list: rfiIds }),
      ...(providerTypes?.length && { provider_type_id_list: providerTypes }),
      ...(indications?.length && { indication_id_list: indications }),
      ...(revenueRangeString?.length && {
        revenue_range_string: revenueRangeString,
      }),
      ...(companySizeFiltersId?.length && {
        employee_count_range_list: companySizeFiltersId,
      }),
      ...(includeRangeRevenue && {
        revenue_range: selectedRevenueRange,
      }),
      ...(includeReportAvailable && { report_availability: reportAvailable }),
      ...(dataAvailability?.length && { data_availability: dataAvailability }),
      ...(searchQuery?.length > 0 && {
        search_text: encodeURIComponent(searchQuery),
      }),
    }),
    [
      currentPage,
      providerIds,
      rfiIds,
      providerTypes,
      indications,
      companySizeFiltersId,
      selectedRevenueRange,
      revenueRangeString,
      includeRangeRevenue,
      reportAvailable,
      dataAvailability,
      includeReportAvailable,
      searchQuery,
    ],
  );
  const { mutate: exportData, error } = useExport();

  const handleExport = () => {
    exportData(payload, {
      onSuccess: () => {
        setShow(true); // Show success notification
        setIsExport(true);
      },
      onError: () => {
        setShow(true); // Show error notification
        setIsExport(false);
        setExportError(error?.message);
      },
    });
    setIsExport(false);
    setOpen(false);
  };

  useEffect(() => {
    if (isSearchExpanded && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchExpanded]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchInputRef.current &&
        !searchInputRef.current.parentElement.contains(event.target)
      ) {
        if (isSearchExpanded && !searchInputValue) {
          setIsSearchExpanded(false);
        }
      }
    };

    if (isSearchExpanded) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isSearchExpanded, searchInputValue]);

  const onVendorFilterChange = useCallback(
    debounce((filters) => {
      dispatch(
        setDiscoveryFilters({
          providerIds: filters.selectedVendors?.map((d) => d.value),
          rfiIds: filters.selectedCategories?.map((cat) => cat.value),
          indications: Object.values(filters.selectedTAIndications)
            ?.map((d) => d.indications)
            .flat()
            .map((d) => d.id),
          providerTypes: filters.selectedVendorType?.map((d) => d.value),
          companySizeFiltersId: filters.selectedEmployeeSizes?.map(
            (d) => d.value,
          ),
          selectedRevenueRange: filters.selectedRevenueRanges?.map(
            (d) => d.value,
          ),
          reportAvailable: {
            is_rfi_available: !!filters.selectedDocAvail?.find(
              (d) => d.value === "rfi",
            ),
            is_audit_report_available: !!filters.selectedDocAvail?.find(
              (d) => d.value === "audit",
            ),
          },
          dataAvailability: filters.selectedDataAvailability?.map(
            (data) => data.value,
          ),
          revenueRangeString: filters.selectedRevenueRanges?.map(
            (d) => d.value,
          ),
          countries:
            filters.locationFilters
              .map((filter) =>
                filter.country?.value === "US" ? "US" : filter.country?.label,
              )
              .filter((f) => f) || [],
          states:
            filters.locationFilters
              .map((filter) => filter.state?.label)
              .filter((f) => f) || [],
          cities:
            filters.locationFilters
              .map((filter) => filter.city)
              .filter((f) => f) || [],
        }),
      );
    }, 300),
    [dispatch],
  );

  const normalizeVendorName = useCallback(
    (name) =>
      (name || "")
        .toString()
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "")
        .replace(/[^a-z0-9]/g, ""),
    []
  );

  const allPagedVendorsSelected = useMemo(() => {
    if (pagedProviders.length === 0) return false;
    const selectedNames = new Set(selectedVendors.map((v) => normalizeVendorName(v.name)));
    return pagedProviders.every((provider) =>
      selectedNames.has(normalizeVendorName(provider.name))
    );
  }, [normalizeVendorName, pagedProviders, selectedVendors]);

  const handleSelectAllCards = useCallback(() => {
    if (allPagedVendorsSelected) {
      const pagedNames = new Set(pagedProviders.map((p) => normalizeVendorName(p.name)));
      setSelectedVendors((prev) =>
        prev.filter((vendor) => !pagedNames.has(normalizeVendorName(vendor.name)))
      );
      return;
    }

    const pagedNames = new Set(pagedProviders.map((p) => normalizeVendorName(p.name)));
    setSelectedVendors((prev) => {
      const existingOtherPage = prev.filter(
        (vendor) => !pagedNames.has(normalizeVendorName(vendor.name))
      );
      const newFromPage = pagedProviders.filter(
        (provider) => !existingOtherPage.some((v) => v.id === provider.id)
      );
      return [...existingOtherPage, ...newFromPage];
    });
  }, [allPagedVendorsSelected, normalizeVendorName, pagedProviders]);

  const onRemoveVendor = (v) =>
    setSelectedVendors(selectedVendors.filter((sel) => sel.id !== v.id));

  const handleCreateListFromSelection = useCallback(() => {
    setModalSelectedVendors(selectedVendors);
    setCreateListModalOpen(true);
  }, [selectedVendors]);

  const handleAddToListFromSelection = useCallback(() => {
    setModalSelectedVendors(selectedVendors);
    setAddToListModalOpen(true);
  }, [selectedVendors]);

  return (
    <div className={`container mx-auto pb-4 bg-gray-50 h-screen`}>
      <header className="px-4 bg-white py-2 pt-6 pb-5 flex flex-col lg:flex-row justify-between items-center space-y-4 lg:space-y-0 lg:space-x-4 shadow-header">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            Vendor Discovery
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            Search the Diligent Vendor Base for clinical trial service
            providers, CROs, CDMOs, and more, based on your trial specifications
             and vendor capabilities. Select desired vendors to request{" "}
            {hideVqq
              ? "audit reports and new auditor-led qualification assessments."
              : "VQQ questionnaires, audit reports, and new auditor-led qualification assessments."}
          </p>
        </div>
        {!hideRequestActions && (
          <div className="w-full lg:w-1/2 mt-5 flex sm:justify-start lg:justify-end">
            <CustomButton
              color={"bg-indigo-600"}
              size={"text-base"}
              weight={"font-normal"}
              height={"h-9"}
              width={"w-[157px]"}
              textColor={"text-white"}
              text={"Request New Vendor"}
              onClick={() => {
                navigate("/Provider/Create");
              }}
            />
          </div>
        )}
      </header>
      <div className="space-y-8 p-8">
        <VendorFilters
          generalSearchQuery={searchInputValue}
          onFilterChange={onVendorFilterChange}
          hideVqq={hideVqq}
        />
      </div>

      <div className="px-8 flex flex-wrap justify-between lg:justify-between items-center">
        <div className="w-full flex items-center gap-4 min-h-[40px]">
          <VendorDiscoverySelectionBar
            canSelect={canSelectVendors}
            canManageMyVendors={canManageMyVendors && Boolean(sponsorId)}
            canCompare={canCompareTrialExperience}
            handleSelectAllCards={handleSelectAllCards}
            allCardsSelected={allPagedVendorsSelected}
            listSelectedVendors={selectedVendors}
            onRemove={onRemoveVendor}
            onCreateList={handleCreateListFromSelection}
            onAddToList={handleAddToListFromSelection}
            onAddToMyVendors={handleBulkAddToMyVendors}
            onBookmarkSelected={handleBookmarkSelected}
            allSelectedBookmarked={allSelectedBookmarked}
            isBookmarkSelectedPending={bulkBookmarkPending}
          />
          <div className="flex flex-wrap lg:justify-end items-center ml-auto">
            <div
              className={`relative flex items-center transition-all duration-300 ease-in-out ${
                isSearchExpanded ? "" : "w-auto"
              }`}
            >
              {isSearchExpanded ? (
                <div className="relative w-full">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <MainSearchBar
                    inputRef={searchInputRef}
                    pageKey="discovery"
                    placeholder="Search all vendor details..."
                    height="38px"
                    width={"16rem"}
                    marginRight={"mr-2"}
                    isSearchIcon={true}
                    onSearch={onSubmit}
                    hideBackIcon
                    className="block w-full rounded-md border-0 py-1.5 pl-9 pr-3 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm sm:leading-6"
                    searchIcon={
                      <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    }
                    onValueChange={onSubmit}
                  />
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setIsSearchExpanded(true)}
                  className="p-2 text-gray-400 hover:text-indigo-600 transition-colors rounded-md hover:bg-gray-100"
                  aria-label="Search filters"
                >
                  <MagnifyingGlassIcon className="h-6 w-6" />
                </button>
              )}
            </div>
            {/* Chat Diligent button: only when user has Access to Chat Diligent permission (all environments) */}
            {hasChatDiligentAccess && <ChatDQPButton />}

            {/* TODO: map view button 
            {cardView === "map" ? (
              <button
                type="button"
                className="p-2 text-indigo-600 transition-colors rounded-md hover:bg-gray-100"
                onClick={() => setCardView("card")}
                aria-label="Grid view"
                title="Grid view"
              >
                <span className="sr-only">View grid</span>
                <Squares2X2Icon className="h-6 w-6" aria-hidden="true" />
              </button>
            ) : (
              <button
                type="button"
                className="p-2 text-gray-400 hover:text-indigo-600 transition-colors rounded-md hover:bg-gray-100"
                onClick={() => setCardView("map")}
                aria-label="Map view"
                title="Map view"
              >
                <span className="sr-only">View map</span>
                <GlobeAltIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            )} */}

            <div>
              {cardView === "list" ? (
                <button
                  type="button"
                  className="p-2 text-gray-400 hover:text-indigo-600 transition-colors rounded-md hover:bg-gray-100"
                  onClick={() => setCardView("card")}
                >
                  <span className="sr-only">View grid</span>
                  <Squares2X2Icon className="h-6 w-6 cursor-pointer" />
                </button>
              ) : (
                <button
                  type="button"
                  className="p-2 text-gray-400 hover:text-indigo-600 transition-colors rounded-md hover:bg-gray-100"
                  onClick={() => setCardView("list")}
                >
                  <span className="sr-only">View list</span>
                  <ListBulletIcon className="h-6 w-6 cursor-pointer" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col px-8 mt-8 bg-gray-50">
        <main
          className="w-full flex flex-wrap justify-center mx-auto pb-2"
          style={{ height: "fit-content" }}
        >
          {isLoading ? (
            <DataLoading />
          ) : (
            <div className="w-full">
              {cardView === "map" ? (
                <ProvidersMapView
                  key={`map-view`}
                  providers={sortedProviders}
                  selectedOptions={selectedVendors}
                  setSelectedOptions={setSelectedVendors}
                  serviceColors={serviceColors}
                />
              ) : cardView === "card" ? (
                <ProviderCardList
                  key={`card-view`}
                  providers={pagedProviders}
                  selectedOptions={selectedVendors}
                  setSelectedOptions={setSelectedVendors}
                  serviceColors={serviceColors}
                  hideRequestActions={hideRequestActions}
                  canSelectVendors={canSelectVendors}
                  hideVqq={hideVqq}
                  {...myVendorDiscoveryProps}
                />
              ) : (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mt-0">
                  <ProvidersListGrid
                    key={`grid-view`}
                    providers={pagedProviders}
                    selectedOptions={selectedVendors}
                    setSelectedOptions={setSelectedVendors}
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                    serviceColors={serviceColors}
                    hideRequestActions={hideRequestActions}
                    canSelectVendors={canSelectVendors}
                    hideVqq={hideVqq}
                    {...myVendorDiscoveryProps}
                  />
                </div>
              )}
            </div>
          )}
        </main>
        {!isLoading && cardView !== "map" && (
          <Pagination
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            className="!bg-gray-50"
          />
        )}
      </div>
      <CustomConfirmModal
        open={open}
        setOpen={setOpen}
        totalItems={totalItems}
        setShow={setShow}
        currentPage={currentPage}
        setIsExport={setIsExport}
        setExportError={setExportError}
        onClick={handleExport}
        title={"Export"}
        desc={`This action will export ${totalItems} record(s) in excel. Do you want to continue?`}
      />

      <Modal
        open={open}
        setOpen={setOpen}
        onClick={handleExport}
        description={`This action will export ${totalItems} record(s) in excel. Do you want to continue?`}
        title={"Export"}
        textBtn={"Export"}
      />

      {isExport && (
        <Notification
          show={show}
          setShow={setShow}
          errorMessage={
            isExport &&
            "Export file is processing. You'll recieve an email when it's ready."
          }
          status={"export"}
          isExport={isExport}
        />
      )}

      {canManageMyVendors && myVendorsNotification.show && (
        <Notification
          show={myVendorsNotification.show}
          setShow={(nextShow) =>
            setMyVendorsNotification((prev) => ({ ...prev, show: nextShow }))
          }
          errorMessage={myVendorsNotification.message}
          status={myVendorsNotification.status}
        />
      )}

      {canManageMyVendors ? (
        <>
          <CreateVendorListModal
            isOpen={createListModalOpen}
            onClose={() => setCreateListModalOpen(false)}
            onCreateList={handleCreateVendorList}
            existingListNames={userVendorLists.map((list) => list.name)}
            initialVendors={modalSelectedVendors}
            isSubmitting={listMutating}
          />

          <AddToListModal
            isOpen={addToListModalOpen}
            onClose={() => setAddToListModalOpen(false)}
            selectedVendors={modalSelectedVendors}
            vendorLists={vendorLists}
            onAddToList={handleAddVendorsToList}
            isSubmitting={addToListSubmitting}
          />

          <AddToMyVendorsModal
            isOpen={addToMyVendorsModalOpen}
            onClose={() => setAddToMyVendorsModalOpen(false)}
            excludeProviderIds={myVendorsProviderIds}
            onAddVendors={handleAddVendorsFromModal}
            isSubmitting={membershipMutating}
          />
        </>
      ) : null}
    </div>
  );
};

export default Discovery;
